import React, { useEffect, useState } from "react";
import { Languages, Loader2 } from "lucide-react";
import translationService from "../services/translationService";
import { useTranslation } from "../i18n";

/**
 * Renders user-written text with a "Translate" affordance underneath.
 *
 * The button shows only when the backend has a translation engine enabled.
 * Clicking it translates into the viewer's language (detected source) and
 * swaps the text; a second click shows the original. If the text turns out
 * to already be in the viewer's language, a short note says so instead.
 *
 * Props: text (plain string), className (applied to the text block),
 * render (optional (text) => node to control how the text is drawn).
 */
const TranslatableText = ({ text, className = "", render }) => {
  const { t, locale, translationEnabled } = useTranslation("feed");
  const [translated, setTranslated] = useState(null);
  const [showTranslated, setShowTranslated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [note, setNote] = useState("");

  // New text or new language: forget the previous translation.
  useEffect(() => {
    setTranslated(null);
    setShowTranslated(false);
    setNote("");
  }, [text, locale]);

  const baseLang = (code) => (code || "").split("-")[0].toLowerCase();

  const onTranslate = async () => {
    if (showTranslated) {
      setShowTranslated(false);
      return;
    }
    if (translated) {
      setShowTranslated(true);
      return;
    }
    setLoading(true);
    setNote("");
    try {
      const result = await translationService.translate(text);
      if (baseLang(result.source_lang) === baseLang(locale)) {
        setNote(t("translate.already_in_language"));
      } else {
        setTranslated(result.text);
        setShowTranslated(true);
      }
    } catch (e) {
      const status = e?.response?.status;
      setNote(
        status === 503 ? t("translate.unavailable") : t("translate.failed")
      );
    } finally {
      setLoading(false);
    }
  };

  const body = showTranslated && translated ? translated : text;

  return (
    <>
      <div className={className}>{render ? render(body) : body}</div>
      {translationEnabled && text && (
        <div className="mt-1.5 flex items-center gap-2 text-xs text-gray-500">
          <button
            type="button"
            onClick={onTranslate}
            disabled={loading}
            className="inline-flex items-center gap-1 hover:text-gray-800 disabled:opacity-60"
          >
            {loading ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Languages className="w-3.5 h-3.5" />
            )}
            {showTranslated ? t("translate.show_original") : t("translate.action")}
          </button>
          {showTranslated && translated && (
            <span className="italic">{t("translate.machine_note")}</span>
          )}
          {note && <span className="italic">{note}</span>}
        </div>
      )}
    </>
  );
};

export default TranslatableText;
